import { createContext, ReactNode, useContext, useState } from "react";
import { ErrorModal } from "../Components/Erro";
import { Loader } from "../Components/Loader";
import { IUsuario } from "../Hooks/UsuarioHook";
import { Api } from "../Utils/Api";

// Defina o tipo de dados do contexto
interface ContextData {
  sistemas: ISistemaCompleto[];
  sistemaFocus: ISistemaCompleto;
  getSistemasPorUsuario: (usuario_id: number) => Promise<void>;
  getSistemaPorId: (sistema_id: number) => Promise<void>;
  libera_bloqueia_sistema_por_usuario: (
    usuario_id: number,
    sistema_id: number,
    liberar: number
  ) => Promise<void>;
}

export interface ISistemaCompleto {
  id: number;
  titulo: string;
  url: string;
  logo: string;
  descricao: string;
  liberado: number;
  usuarios: IUsuario[];
  liberado_todos:boolean;
}

// Crie o contexto com um valor inicial vazio
const SistemaStorageContext = createContext<ContextData>({} as ContextData);

// Crie um hook personalizado para utilizar o contexto
function useSistemaStorage() {
  const context = useContext(SistemaStorageContext);
  if (!context) {
    throw new Error(
      "useSistemaStorage deve ser usado dentro de um SistemaStorageProvider"
    );
  }
  return context;
}

// Provedor do contexto
interface IProviderProps {
  children: ReactNode;
}
const path_api = "/sistema";
function SistemaStorageProvider({ children }: IProviderProps) {
  const [loading, setLoading] = useState(false);
  const [alerta, setAlerta] = useState({ titulo: "", descricao: "" });
  const [sistemas, setSistemas] = useState([] as ISistemaCompleto[]);
  const [sistemaFocus, setSistemaFocus] = useState({} as ISistemaCompleto);

  async function getSistemasPorUsuario(usuario_id: number) {
    try {
      setLoading(true);
      const { data } = await Api.get(`${path_api}/por-usuario/${usuario_id}`);
      setSistemas(data.data);
      setAlerta({
        titulo: "Erro ao carregar sistemas pelo usuario!",
        descricao: data.mensagem,
      });
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar os sistemas",
        descricao: "Não foi possível carregar os sistemas cadastrados!",
      });
    } finally {
      setLoading(false);
    }
  }

  async function getSistemaPorId(sistema_id: number) {
    try {
      setLoading(true);
      const { data } = await Api.get(`${path_api}/detalhes/${sistema_id}`);
      setSistemaFocus(data.data);
      setAlerta({
        titulo: "Erro ao carregar sistemas pelo id!",
        descricao: data.mensagem,
      });
    } catch (e) {
      setAlerta({
        titulo: "Erro ao carregar os sistemas",
        descricao: "Não foi possível carregar os sistemas cadastrados!",
      });
    } finally {
      setLoading(false);
    }
  }

  async function libera_bloqueia_sistema_por_usuario(
    usuario_id: number,
    sistema_id: number,
    liberar: number
  ) {
    try {
      setLoading(true);

      if (!liberar) {
        const { data } = await Api.post(`${path_api}-usuario`, {
          usuario_id,
          sistema_id,
        });
      } else {
        const { data } = await Api.delete(`${path_api}-usuario/${liberar}`);
      }
    } catch (e) {
      console.log(e);
      setAlerta({
        titulo: "Erro ao carregar os sistemas",
        descricao: "Não foi possível carregar os sistemas cadastrados!",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <SistemaStorageContext.Provider
      value={{
        sistemas,
        getSistemasPorUsuario,
        libera_bloqueia_sistema_por_usuario,
        getSistemaPorId,
        sistemaFocus,
      }}
    >
      <Loader loading={loading} />
      <ErrorModal
        description={alerta.descricao}
        title={alerta.titulo}
        onClose={() => setAlerta({ descricao: "", titulo: "" })}
        open={alerta.titulo.length > 0 && alerta.descricao.length > 0}
      />
      {children}
    </SistemaStorageContext.Provider>
  );
}

export { SistemaStorageProvider, useSistemaStorage };
